import { useState, type CSSProperties } from 'react'
import type { RestockChip } from '../../lib/restock'
import { MOTION, HAPTICS } from '../../lib/constants'
import { haptic } from '../../lib/haptics'
import { PlusIcon } from '../icons'

// "Running low" (ux/03 RestockChip): hairline-outlined, transparent chips for
// what this household buys regularly and is due again. Outline-only means
// "not on the list yet". A tap fades the chip out, then adds the item.

export function RestockChips({ chips, tintFor, layout, onAdd }: {
  chips: RestockChip[]
  tintFor: (categoryId: string | null) => string
  layout: 'phone' | 'desktop'
  onAdd: (chip: RestockChip) => void
}) {
  const [going, setGoing] = useState<Set<string>>(new Set())
  if (!chips.length) return null

  function tap(chip: RestockChip) {
    if (going.has(chip.key)) return
    haptic(HAPTICS.chip)
    setGoing(g => new Set(g).add(chip.key))
    setTimeout(() => {
      onAdd(chip)
      setGoing(g => { const next = new Set(g); next.delete(chip.key); return next })
    }, MOTION.chipOut.addAfterMs)
  }

  return (
    <section className={`ml-restock ml-restock--${layout}`} aria-labelledby="ml-restock-label">
      <h2 id="ml-restock-label" className="ml-label ml-section-label">Running low</h2>
      <ul className="ml-restock__chips">
        {chips.map(chip => (
          <li key={chip.key}>
            <button type="button" className={`ml-chip${going.has(chip.key) ? ' is-going' : ''}`}
              style={{ '--ml-tint': tintFor(chip.categoryId) } as CSSProperties}
              aria-label={`Add ${chip.name}, ${chip.hint.toLowerCase()}`}
              onClick={() => tap(chip)}>
              <span className="ml-chip__plus" aria-hidden="true"><PlusIcon size={16} /></span>
              <span className="ml-chip__text">
                <span className="ml-chip__name">{chip.name}</span>
                <span className="ml-chip__hint">{chip.hint}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
